/**
 * banfFilters — gemeinsame Filter-Logik für purchase_requests (BANF).
 * Genutzt von der BANF-Liste (banfList/BanfListView) und perspektivisch vom
 * KPI-Dashboard. Hält die Feldzuordnung an einer Stelle, damit Liste + Dashboard
 * konsistent filtern. Alles client-seitig auf bereits geladenen BANFs.
 *
 * Filter-Keys (jeweils string[]; leer = „alle"):
 *   status · anforderer · kostl · abteilung · year · ekgrp · matkl · werk
 *   · material · lieferant · belegart · kontierung   (ME5A-orientiert)
 * Positions-Keys (items[]): BANF zählt, wenn EINE Position passt.
 */

export const itemEkgrp = (it) => String(it.einkauefergruppe || it.einkaeufergruppe || it.purchasing_group || '');
export const itemMatkl = (it) => String(it.material_group || it.product_group || it.category || '');
export const itemWerk = (it) => String(it.plant || it.werk || '');
export const itemMaterial = (it) => String(it.matnr || it.material || '');
// Lieferant kann in drei Feldern stehen (fester Lieferant, Wunschlieferant, OCI-vendor) —
// alle nicht-leeren Werte zählen (Codes UND Namen, je nachdem was erfasst wurde).
export const itemLieferanten = (it) => [it.fixed_vendor, it.preferred_vendor, it.vendor]
  .map((v) => String(v || '').trim()).filter(Boolean);
export const itemBelegart = (it) => String(it.belegart || '');
export const itemKontierung = (it) => String(it.kontierungstyp || '');
// Anforderer = das in der Karte angezeigte Feld (requester_name), Fallback User-Name.
export const reqAnforderer = (r) => (r.requester_name && r.requester_name.trim()) || (r.requester_full_name && r.requester_full_name.trim()) || '';

const uniqSort = (set) => [...set].sort();

export function deriveBanfFilterOptions(requests) {
  const anforderer = new Set(), kostl = new Set(), abteilung = new Set(),
    ekgrp = new Set(), matkl = new Set(), werk = new Set(), years = new Set(), statuses = new Set(),
    material = new Set(), lieferant = new Set(), belegart = new Set(), kontierung = new Set();
  (requests || []).forEach((r) => {
    if (reqAnforderer(r)) anforderer.add(reqAnforderer(r));
    if (r.requester_cost_center) kostl.add(String(r.requester_cost_center));
    if (r.requester_department) abteilung.add(String(r.requester_department));
    if (r.status) statuses.add(r.status);
    const d = r.created_at ? new Date(r.created_at) : null;
    if (d && !isNaN(d.getTime())) years.add(String(d.getFullYear()));
    (Array.isArray(r.items) ? r.items : []).forEach((it) => {
      if (itemEkgrp(it)) ekgrp.add(itemEkgrp(it));
      if (itemMatkl(it)) matkl.add(itemMatkl(it));
      if (itemWerk(it)) werk.add(itemWerk(it));
      if (itemMaterial(it)) material.add(itemMaterial(it));
      itemLieferanten(it).forEach((v) => lieferant.add(v));
      if (itemBelegart(it)) belegart.add(itemBelegart(it));
      if (itemKontierung(it)) kontierung.add(itemKontierung(it));
    });
  });
  return {
    anforderer: uniqSort(anforderer), kostl: uniqSort(kostl), abteilung: uniqSort(abteilung),
    ekgrp: uniqSort(ekgrp), matkl: uniqSort(matkl), werk: uniqSort(werk),
    material: uniqSort(material), lieferant: uniqSort(lieferant),
    belegart: uniqSort(belegart), kontierung: uniqSort(kontierung),
    years: uniqSort(years).reverse(), statuses: uniqSort(statuses),
  };
}

export function applyBanfFilters(requests, f = {}) {
  const arr = (k) => (Array.isArray(f[k]) ? f[k] : []);
  return (requests || []).filter((r) => {
    if (arr('status').length && !arr('status').includes(r.status)) return false;
    if (arr('anforderer').length && !arr('anforderer').includes(reqAnforderer(r))) return false;
    if (arr('kostl').length && !arr('kostl').includes(String(r.requester_cost_center))) return false;
    if (arr('abteilung').length && !arr('abteilung').includes(String(r.requester_department))) return false;
    if (arr('year').length) {
      const d = r.created_at ? new Date(r.created_at) : null;
      if (!d || isNaN(d.getTime()) || !arr('year').includes(String(d.getFullYear()))) return false;
    }
    const items = Array.isArray(r.items) ? r.items : [];
    if (arr('ekgrp').length && !items.some((it) => arr('ekgrp').includes(itemEkgrp(it)))) return false;
    if (arr('matkl').length && !items.some((it) => arr('matkl').includes(itemMatkl(it)))) return false;
    if (arr('werk').length && !items.some((it) => arr('werk').includes(itemWerk(it)))) return false;
    if (arr('material').length && !items.some((it) => arr('material').includes(itemMaterial(it)))) return false;
    if (arr('lieferant').length && !items.some((it) => itemLieferanten(it).some((v) => arr('lieferant').includes(v)))) return false;
    if (arr('belegart').length && !items.some((it) => arr('belegart').includes(itemBelegart(it)))) return false;
    if (arr('kontierung').length && !items.some((it) => arr('kontierung').includes(itemKontierung(it)))) return false;
    return true;
  });
}
